import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getSessionToken } from "../hooks/useSession";
import { Button } from "./ui/Button";
import { DataTableSkeleton } from "./ui/DataTableSkeleton";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface ReaganSyncRun {
  id: string;
  triggerType: string;
  requestedBy: string | null;
  status: string;
  fetchedCount: number;
  upsertedCount: number;
  fetchedContractCount: number;
  upsertedContractCount: number;
  fetchedOnboardingCount: number;
  upsertedOnboardingCount: number;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
}

interface ReaganAgentRecord {
  id: string;
  externalId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  status: string | null;
  uplineName: string | null;
  matchedSubmissionId: string | null;
  matchedBy: string | null;
  matchedAt: string | null;
  lastSyncedAt: string;
}

interface ReaganContractRecord {
  id: string;
  externalAgentId: string;
  carrierName: string | null;
  writingNo: string | null;
  statusName: string | null;
  contractDate: string | null;
  termDate: string | null;
  lastSyncedAt: string;
}

interface ReaganOnboardingRecord {
  id: string;
  externalAgentId: string;
  name: string | null;
  uplineName: string | null;
  statusName: string | null;
  hasDebt: boolean | null;
  currentStep: string | null;
  statusDate: string | null;
  lastSyncedAt: string;
}

function fmt(value: string | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

export function ReaganData() {
  const token = useMemo(() => getSessionToken(), []);
  const [loading, setLoading] = useState(true);
  const [runningSync, setRunningSync] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runs, setRuns] = useState<ReaganSyncRun[]>([]);
  const [agents, setAgents] = useState<ReaganAgentRecord[]>([]);
  const [contracts, setContracts] = useState<ReaganContractRecord[]>([]);
  const [onboarding, setOnboarding] = useState<ReaganOnboardingRecord[]>([]);

  async function loadAll() {
    if (!token) {
      setError("You need to log in before viewing Reagan data.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const headers = { Authorization: `Bearer ${token}` };
      const [runsRes, agentsRes, contractsRes, onboardingRes] = await Promise.all([
        fetch(`${API_BASE}/api/reagan/sync/runs?limit=25`, { headers }),
        fetch(`${API_BASE}/api/reagan/sync/agents?limit=500`, { headers }),
        fetch(`${API_BASE}/api/reagan/sync/contracts?limit=1000`, { headers }),
        fetch(`${API_BASE}/api/reagan/sync/onboarding?limit=1000`, { headers }),
      ]);

      if (!runsRes.ok || !agentsRes.ok || !contractsRes.ok || !onboardingRes.ok) {
        throw new Error("Failed to load Reagan data.");
      }

      const [runsJson, agentsJson, contractsJson, onboardingJson] = await Promise.all([
        runsRes.json(),
        agentsRes.json(),
        contractsRes.json(),
        onboardingRes.json(),
      ]);

      setRuns((runsJson.data ?? []) as ReaganSyncRun[]);
      setAgents((agentsJson.data ?? []) as ReaganAgentRecord[]);
      setContracts((contractsJson.data ?? []) as ReaganContractRecord[]);
      setOnboarding((onboardingJson.data ?? []) as ReaganOnboardingRecord[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load Reagan data.");
    } finally {
      setLoading(false);
    }
  }

  async function runSyncNow() {
    if (!token) return;
    try {
      setRunningSync(true);
      setError(null);
      const res = await fetch(`${API_BASE}/api/reagan/sync/run`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || "Failed to start Reagan sync job.");
      }
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to start sync.");
    } finally {
      setRunningSync(false);
    }
  }

  useEffect(() => {
    loadAll();
    const id = setInterval(loadAll, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-accent">Upstream cache</p>
            <h3 className="text-xl font-semibold text-content-primary">Reagan records</h3>
            <p className="mt-1 text-sm text-content-secondary">
              Synced agents, contracts, onboarding rows, and link to submissions.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={runSyncNow}
              disabled={runningSync}
            >
              {runningSync ? "Starting sync…" : "Run sync now"}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={loadAll}
            >
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div role="alert" className="rounded-lg border border-status-rejected-border bg-status-rejected-bg p-3 text-sm text-status-rejected-text">
            {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-4" role="status" aria-busy="true">
            <DataTableSkeleton headers={["Started", "Status", "Trigger", "Agents", "Contracts", "Onboarding"]} cellVariants={["text", "badge", "text", "text", "text", "text"]} rows={3} loadingLabel="Loading Reagan sync runs" />
            <DataTableSkeleton headers={["Agent", "Email", "Upline", "Matched submission", "Matched by", "Last synced"]} cellVariants={["multiline", "text", "multiline", "text", "text", "text"]} rows={5} loadingLabel="Loading Reagan agents" />
          </div>
        ) : (
          <>
            <section className="rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-4 shadow-card">
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-content-muted">Sync runs</h4>
              <div className="overflow-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <thead className="text-content-secondary">
                    <tr>
                      <th className="text-left py-2 pr-3">Started</th>
                      <th className="text-left py-2 pr-3">Status</th>
                      <th className="text-left py-2 pr-3">Trigger</th>
                      <th className="text-left py-2 pr-3">Agents</th>
                      <th className="text-left py-2 pr-3">Contracts</th>
                      <th className="text-left py-2 pr-3">Onboarding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((run) => (
                      <tr key={run.id} className="border-t border-border-subtle text-content-secondary">
                        <td className="py-2 pr-3">{fmt(run.startedAt)}</td>
                        <td className="py-2 pr-3">{run.status}</td>
                        <td className="py-2 pr-3">{run.triggerType}</td>
                        <td className="py-2 pr-3">{run.upsertedCount}/{run.fetchedCount}</td>
                        <td className="py-2 pr-3">{run.upsertedContractCount}/{run.fetchedContractCount}</td>
                        <td className="py-2 pr-3">{run.upsertedOnboardingCount}/{run.fetchedOnboardingCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-4 shadow-card">
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-content-muted">Agents <span className="text-content-primary">({agents.length})</span></h4>
              <div className="overflow-auto">
                <table className="w-full min-w-[920px] text-sm">
                  <thead className="text-content-secondary">
                    <tr>
                      <th className="text-left py-2 pr-3">Agent</th>
                      <th className="text-left py-2 pr-3">Email</th>
                      <th className="text-left py-2 pr-3">Upline</th>
                      <th className="text-left py-2 pr-3">Matched Submission</th>
                      <th className="text-left py-2 pr-3">Matched By</th>
                      <th className="text-left py-2 pr-3">Last Synced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((agent) => (
                      <tr key={agent.id} className="border-t border-border-subtle text-content-secondary">
                        <td className="py-2 pr-3">
                          {(agent.firstName || "") + " " + (agent.lastName || "")} ({agent.externalId})
                        </td>
                        <td className="py-2 pr-3">{agent.email || "—"}</td>
                        <td className="py-2 pr-3">{agent.uplineName || "—"}</td>
                        <td className="py-2 pr-3">
                          {agent.matchedSubmissionId ? (
                            <Link to={`/dashboard/submissions/${agent.matchedSubmissionId}`} className="text-accent hover:opacity-80">
                              {agent.matchedSubmissionId.slice(0, 8)}...
                            </Link>
                          ) : "—"}
                        </td>
                        <td className="py-2 pr-3">{agent.matchedBy || "—"}</td>
                        <td className="py-2 pr-3">{fmt(agent.lastSyncedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-4 shadow-card">
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-content-muted">Contracts <span className="text-content-primary">({contracts.length})</span></h4>
              <div className="overflow-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <thead className="text-content-secondary">
                    <tr>
                      <th className="text-left py-2 pr-3">Agent Id</th>
                      <th className="text-left py-2 pr-3">Carrier</th>
                      <th className="text-left py-2 pr-3">Writing #</th>
                      <th className="text-left py-2 pr-3">Status</th>
                      <th className="text-left py-2 pr-3">Last Update</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((row) => (
                      <tr key={row.id} className="border-t border-border-subtle text-content-secondary">
                        <td className="py-2 pr-3">{row.externalAgentId}</td>
                        <td className="py-2 pr-3">{row.carrierName || "—"}</td>
                        <td className="py-2 pr-3">{row.writingNo || "—"}</td>
                        <td className="py-2 pr-3">{row.statusName || "—"}</td>
                        <td className="py-2 pr-3">{fmt(row.contractDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-4 shadow-card">
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-content-muted">Onboarding <span className="text-content-primary">({onboarding.length})</span></h4>
              <div className="overflow-auto">
                <table className="w-full min-w-[920px] text-sm">
                  <thead className="text-content-secondary">
                    <tr>
                      <th className="text-left py-2 pr-3">Agent Id</th>
                      <th className="text-left py-2 pr-3">Name</th>
                      <th className="text-left py-2 pr-3">Upline</th>
                      <th className="text-left py-2 pr-3">Status</th>
                      <th className="text-left py-2 pr-3">Step</th>
                      <th className="text-left py-2 pr-3">Debt</th>
                      <th className="text-left py-2 pr-3">Last Update</th>
                    </tr>
                  </thead>
                  <tbody>
                    {onboarding.map((row) => (
                      <tr key={row.id} className="border-t border-border-subtle text-content-secondary">
                        <td className="py-2 pr-3">{row.externalAgentId}</td>
                        <td className="py-2 pr-3">{row.name || "—"}</td>
                        <td className="py-2 pr-3">{row.uplineName || "—"}</td>
                        <td className="py-2 pr-3">{row.statusName || "—"}</td>
                        <td className="py-2 pr-3">{row.currentStep || "—"}</td>
                        <td className="py-2 pr-3">
                          {row.hasDebt === null ? "—" : row.hasDebt ? "Yes" : "No"}
                        </td>
                        <td className="py-2 pr-3">{fmt(row.statusDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
    </div>
  );
}
